import * as Sentry from "@sentry/react";
import { MediaError } from "../errors/sentryErrors";

const UNSPLASH_ACCESS_KEY = import.meta.env.VITE_UNSPLASH_ACCESS_KEY;
const PEXELS_API_KEY = import.meta.env.VITE_PEXELS_API_KEY;

const imageCache = new Map();

let requestQueue = Promise.resolve();
const DELAY_BETWEEN_REQUESTS = 200;

const throttle = () => {
  requestQueue = requestQueue.then(
    () => new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_REQUESTS))
  );
  return requestQueue;
};

const searchUnsplash = async (query, count = 5, page = 1) => {
  if (!UNSPLASH_ACCESS_KEY) return [];

  await throttle();

  try {
    const response = await fetch(
      `https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=${count}&page=${page}&orientation=landscape`,
      { headers: { 'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}` } }
    );

    if (!response.ok) return [];

    const data = await response.json();
    return data.results.map(photo => ({
      id: photo.id,
      url: photo.urls.regular,
      thumbnail: photo.urls.small,
      alt: photo.alt_description || query,
      photographer: photo.user.name,
      source: 'Unsplash'
    }));
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "media");
      scope.setTag("app", "web");
      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
      Sentry.captureException(new MediaError(error instanceof Error ? error.message : String(error)));
    });
    console.error('Unsplash error:', error);
    return [];
  }
};

const searchPexels = async (query, count = 5, page = 1) => {
  if (!PEXELS_API_KEY || PEXELS_API_KEY.includes('YOUR_')) return [];

  await throttle();

  try {
    const response = await fetch(
      `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}&per_page=${count}&page=${page}&orientation=landscape`,
      { headers: { 'Authorization': PEXELS_API_KEY } }
    );

    if (!response.ok) return [];

    const data = await response.json();
    return data.photos.map(photo => ({
      id: `pexels-${photo.id}`,
      url: photo.src.large2x || photo.src.large,
      thumbnail: photo.src.medium,
      alt: photo.alt || query,
      photographer: photo.photographer,
      source: 'Pexels'
    }));
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "media");
      scope.setTag("app", "web");
      scope.setTag("error_source", error instanceof TypeError ? "network" : "server");
      Sentry.captureException(new MediaError(error instanceof Error ? error.message : String(error)));
    });
    console.error('Pexels error:', error);
    return [];
  }
};

const fetchImages = async (query, count = 5, page = 1) => {
  let images = await searchPexels(query, count, page);

  if (images.length < count) {
    const unsplashImages = await searchUnsplash(query, count - images.length, page);
    images = [...images, ...unsplashImages];
  }

  return images.slice(0, count);
};

const extractPlaceName = (title) => {
  if (!title) return '';

  return title
    .replace(/^(visit|explore|tour|see|go to|arrive at|check-in at|check-out from|arrival and|departure from|transfer to|drive to|walk to|head to)\s+/i, '')
    .replace(/\s+(visit|tour|exploration|excursion)$/i, '')
    .replace(/^(the|a|an)\s+/i, '')
    .trim();
};

const buildSearchQuery = (activity, destination, preferences = {}, activityIndex = 0) => {
  const title = activity.title || '';
  const category = activity.category || '';
  const titleLower = title.toLowerCase();
  const location = destination?.split(',')[0]?.trim() || '';

  const dietary = preferences?.dietaryPreferences || preferences?.dietary || '';
  const isVeg = dietary.toLowerCase().includes('vegetarian') || dietary.toLowerCase().includes('vegan');

  const placeName = extractPlaceName(title);

  switch (category) {
    case 'transport':
      if (titleLower.includes('arrival') || titleLower.includes('airport') || titleLower.includes('flight')) {
        return location ? `${location} airport terminal` : 'airport terminal departure arrival';
      }
      if (titleLower.includes('ferry') || titleLower.includes('boat')) {
        return location ? `${location} ferry boat` : 'ferry boat sea travel';
      }
      if (titleLower.includes('train') || titleLower.includes('railway')) {
        return location ? `${location} railway station train` : 'train railway station platform';
      }
      if (titleLower.includes('check-out') || titleLower.includes('departure')) {
        return location ? `${location} airport departure` : 'airport departure luggage travel';
      }
      if (titleLower.includes('cruise')) {
        return location ? `${location} cruise ship` : 'cruise ship ocean travel';
      }
      return location ? `${location} transportation taxi` : 'taxi transportation travel city';

    case 'accommodation':
      if (titleLower.includes('check-out')) {
        return 'hotel checkout lobby';
      }
      if (titleLower.includes('check-in')) {
        return 'hotel reception lobby';
      }
      return 'hotel room luxury interior';

    case 'dining':
      if (titleLower.includes('breakfast')) {
        return isVeg
          ? `vegetarian breakfast ${location}`.trim()
          : `breakfast food ${location}`.trim();
      }
      if (titleLower.includes('lunch')) {
        return isVeg
          ? `vegetarian lunch ${location}`.trim()
          : `lunch food ${location}`.trim();
      }
      if (titleLower.includes('dinner')) {
        return isVeg
          ? `vegetarian dinner restaurant ${location}`.trim()
          : `dinner restaurant ${location}`.trim();
      }
      if (titleLower.includes('seafood')) {
        return location ? `seafood restaurant ${location}` : 'seafood restaurant fresh';
      }
      return isVeg
        ? `vegetarian food restaurant ${location}`.trim()
        : `food restaurant cuisine ${location}`.trim();

    case 'sightseeing':
      if (placeName) {
        return `${placeName} ${location}`.trim();
      }
      return `${location} tourist attraction landmark`;

    case 'adventure':
      if (placeName) {
        return `${placeName} ${location}`.trim();
      }
      return `${location} adventure activity`;

    case 'shopping':
      if (placeName) {
        return `${placeName} ${location} shopping`.trim();
      }
      return `${location} market shopping`;

    case 'leisure':
      if (placeName) {
        return `${placeName} ${location}`.trim();
      }
      return `${location} relaxation nature`;

    default:
      if (placeName) {
        return `${placeName} ${location}`.trim();
      }
      return `${location} travel tourism`;
  }
};

export const getActivityImages = async (activity, destination, count = 5, activityIndex = 0, preferences = {}) => {
  const cacheKey = `${activityIndex}-${activity.title}-${activity.time}`.toLowerCase();

  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey);
  }

  const query = buildSearchQuery(activity, destination, preferences, activityIndex);

  const page = activityIndex + 1;

  let images = await fetchImages(query, count, page);

  if (images.length === 0) {
    const category = activity.category || '';
    let fallbackQuery = '';

    if (category === 'dining') {
      fallbackQuery = 'indian food restaurant meal';
    } else if (category === 'accommodation') {
      fallbackQuery = 'hotel room interior';
    } else if (category === 'sightseeing') {
      fallbackQuery = `${destination} famous landmark monument`;
    } else if (category === 'transport') {
      fallbackQuery = 'travel transportation taxi';
    } else {
      fallbackQuery = `${destination} travel tourism`;
    }

    images = await fetchImages(fallbackQuery, count, page + 1);
  }

  if (images.length === 0) {
    images = await fetchImages('travel India tourism', count, 1);
  }

  if (images.length > 0) {
    imageCache.set(cacheKey, images);
  }

  return images;
};

export const clearImageCache = () => {
  imageCache.clear();
  requestQueue = Promise.resolve();
};

export default { getActivityImages, clearImageCache };
